import PropTypes from "prop-types"
import { useContext } from "react"
import { Paper, Skeleton, Divider } from "@mui/material"
import ShowNotHeldButton from "modules/rebalance/components/show-notHeld-button"
import ExpansionMenu from "components/expansion-menu"
import GroupingMenu from "components/grouping-menu"
import AssetsViewSelector from "connected-components/dashboard/components/view-selectors/assets"
import ListItemDropdown from "components/list-item-dropdown"
import PeriodDisplay from "connected-components/dashboard/components/period-display"
import "./style.scss"

export default function AssetViewToolbar({
  context,
  templates,
  contentSets,
  isCustomTemplate,
  headerContent,
  columnOrder,
  setColumnOrder,
  columnOrderOptions,
  base,
  showNotHeld,
  setShowNotHeld,
  groupBy,
  handleGroupingChange,
  availableGroupByOptions,
  hasMultipleInstruments,
  isExpanded,
  handleExpansionToggle,
  hasUnexpandedComposites,
  isTemplatesLoading,
  isGroupingLoading,
  hasPerformanceMetrics = false,
}) {
  const { isPreferencesLoaded } = useContext(context)
  const shouldDisableButtons = isTemplatesLoading || isGroupingLoading || !isPreferencesLoaded

  const templatesSelector = isTemplatesLoading ? (
    <Skeleton variant="rectangular" width={180} height="2rem" />
  ) : (
    <AssetsViewSelector context={context} templates={templates} contentSets={contentSets} />
  )

  const headerContentComponent = (headerContent && !isTemplatesLoading && isPreferencesLoaded) ? headerContent : null

  const columnOrderDropdown = isTemplatesLoading ? (
    <Skeleton variant="rectangular" width={140} height="2rem" />
  ) : (
    <ListItemDropdown
      label="Column order"
      value={columnOrder}
      setValue={setColumnOrder}
      options={columnOrderOptions}
      alignMenu="left"
      disablePortal
    />
  )

  return (
    <Paper square className="asset-view-toolbar">
      <div className="asset-view-toolbar__left">
        {templatesSelector}
        <Divider orientation="vertical" flexItem />
        {hasPerformanceMetrics && <PeriodDisplay context={context} />}
        {!isCustomTemplate && headerContentComponent}
        {columnOrderOptions && columnOrderDropdown}
      </div>
      <div className="asset-view-toolbar__right">
        {isCustomTemplate && headerContentComponent}
        {base && (
          <ShowNotHeldButton
            includeNotHeld={showNotHeld}
            toggleNotHeld={() => { setShowNotHeld(!showNotHeld) }}
            disabled={shouldDisableButtons}
          />
        )}
        <GroupingMenu
          setGrouping={handleGroupingChange}
          currentGrouping={groupBy}
          availableRollups={availableGroupByOptions}
          hasMultipleInstruments={hasMultipleInstruments}
          disabled={shouldDisableButtons}
        />
        <ExpansionMenu
          setExpansionMode={handleExpansionToggle}
          isExpanded={isExpanded}
          hasUnexpandedComposites={hasUnexpandedComposites}
          disabled={shouldDisableButtons}
        />
      </div>
    </Paper>
  )
}

AssetViewToolbar.propTypes = {
  context: PropTypes.object.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object),
  contentSets: PropTypes.arrayOf(PropTypes.object).isRequired,
  isCustomTemplate: PropTypes.bool.isRequired,
  headerContent: PropTypes.node,
  columnOrder: PropTypes.string.isRequired,
  setColumnOrder: PropTypes.func.isRequired,
  columnOrderOptions: PropTypes.arrayOf(PropTypes.object),
  base: PropTypes.object,
  showNotHeld: PropTypes.bool.isRequired,
  setShowNotHeld: PropTypes.func.isRequired,
  groupBy: PropTypes.object,
  handleGroupingChange: PropTypes.func.isRequired,
  availableGroupByOptions: PropTypes.arrayOf(PropTypes.object),
  hasMultipleInstruments: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  handleExpansionToggle: PropTypes.func.isRequired,
  hasUnexpandedComposites: PropTypes.bool.isRequired,
  isTemplatesLoading: PropTypes.bool.isRequired,
  isGroupingLoading: PropTypes.bool.isRequired,
  hasPerformanceMetrics: PropTypes.bool,
}
